import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Boxes, Users, UserCog, IndianRupee, Package, Truck, ClipboardList,
  BarChart3, FolderOpen, Headphones, GraduationCap, ShoppingCart,
  Smartphone, Cpu, Code2,
  LayoutGrid, SlidersHorizontal, ShieldCheck, Rocket, ThumbsUp, Phone,
  Briefcase, MessageSquare, Server, Share2, TrendingUp,
  ArrowRight, ChevronRight,
} from 'lucide-react';

const CONTAINER = 'w-full max-w-7xl xl:max-w-[95%] 2xl:max-w-[85vw] mx-auto px-6 sm:px-8 lg:px-8';

/* Resize the hero image with the values below. On desktop it's an ABSOLUTE overlay,
   so making it bigger only overlaps — the text and everything below never move.
   (If you enlarge it a lot and it clips, bump the `lg:min-h-[500px]` on its wrapper div.) */
const HERO_IMAGE = {
  desktopWidth: '660px',   // ← increase this to make the image bigger on desktop
  desktopHeight: 'auto',  // ← 'auto' keeps the ratio, or set e.g. '500px'
  offsetX: '0px',         // ← horizontal nudge (negative = push right / off edge, positive = pull left)
  offsetY: '0px',         // ← vertical nudge, e.g. '-20px'
};

/* ============================== DATA ============================== */

const FEATURES = [
  { Icon: LayoutGrid, top: 'Business Focused', bottom: 'Solutions that solve real business problems' },
  { Icon: SlidersHorizontal, top: 'Scalable & Flexible', bottom: 'Built to scale with your business growth' },
  { Icon: ShieldCheck, top: 'Secure & Reliable', bottom: 'Enterprise-grade security and performance' },
  { Icon: Cpu, top: 'Future Ready', bottom: 'AI-powered and built for the future' },
];

const CATEGORIES = [
  { label: 'All Solutions', Icon: LayoutGrid },
  { label: 'Business Operations', Icon: Briefcase },
  { label: 'Management Systems', Icon: ClipboardList },
  { label: 'Customer Engagement', Icon: MessageSquare },
  { label: 'Analytics & Intelligence', Icon: BarChart3 },
  { label: 'Infrastructure', Icon: Server },
  { label: 'Collaboration', Icon: Share2 },
];

const SOLUTIONS = [
  { Icon: Boxes, color: '#3284c7', title: 'Enterprise Resource Planning (ERP)', category: 'Business Operations', desc: 'Integrated modules to manage your entire business from a single platform.' },
  { Icon: Users, color: '#16a34a', title: 'Customer Relationship Management (CRM)', category: 'Customer Engagement', desc: 'Build stronger relationships, manage leads, and grow your customer base.' },
  { Icon: UserCog, color: '#9333ea', title: 'Human Resource Management (HRMS)', category: 'Management Systems', desc: 'Automate HR processes, payroll, attendance, leave, and employee lifecycle.' },
  { Icon: IndianRupee, color: '#f29132', title: 'Finance & Accounting Solutions', category: 'Business Operations', desc: 'Complete financial management with accounting, GST, invoicing, and reporting.' },
  { Icon: Package, color: '#0d9488', title: 'Inventory & Supply Chain Management', category: 'Business Operations', desc: 'Real-time inventory tracking, procurement, warehouse, and supply chain control.' },
  { Icon: Truck, color: '#e11d48', title: 'Sales & Distribution Management', category: 'Business Operations', desc: 'Manage sales orders, distribution channels, field teams, and performance.' },
  { Icon: ClipboardList, color: '#3284c7', title: 'Project Management Solution', category: 'Management Systems', desc: 'Plan, track, and deliver projects on time with budgets, tasks and resources.' },
  { Icon: BarChart3, color: '#f59e0b', title: 'Business Intelligence & Analytics', category: 'Analytics & Intelligence', desc: 'Powerful dashboards and reports to track KPIs and make smarter decisions.' },
  { Icon: FolderOpen, color: '#9333ea', title: 'Document Management System (DMS)', category: 'Collaboration', desc: 'Securely store, manage, and retrieve documents from anywhere.' },
  { Icon: Headphones, color: '#16a34a', title: 'Customer Support & Helpdesk', category: 'Customer Engagement', desc: 'Manage tickets, track issues, and deliver exceptional customer support.' },
  { Icon: GraduationCap, color: '#3284c7', title: 'Learning Management System (LMS)', category: 'Management Systems', desc: 'Deliver online learning, track progress, and manage courses and assessments.' },
  { Icon: ShoppingCart, color: '#f29132', title: 'eCommerce & Marketplace Solutions', category: 'Business Operations', desc: 'Launch powerful online stores and multi-vendor marketplaces with ease.' },
  { Icon: Smartphone, color: '#0d9488', title: 'Mobile Workforce Management', category: 'Collaboration', desc: 'Manage field teams, track activities, GPS, attendance and performance.' },
  { Icon: Cpu, color: '#e11d48', title: 'AI & Automation Solutions', category: 'Analytics & Intelligence', desc: 'Leverage AI, chatbots, RPA and automation to accelerate your business.' },
  { Icon: Code2, color: '#2563eb', title: 'Custom Software Solutions', category: 'Infrastructure', desc: 'Tailor-made software built to match your unique business requirements.' },
];

const STATS = [
  { Icon: Rocket, value: '10+', label: 'Successful Deployments' },
  { Icon: Boxes, value: '50+', label: 'Ready-to-Use Modules' },
  { Icon: ShieldCheck, value: '99.9%', label: 'System Uptime' },
  { Icon: Users, value: '10+', label: 'Expert Professionals' },
  { Icon: Headphones, value: '24/7', label: 'Support & Maintenance' },
  { Icon: ThumbsUp, value: '100%', label: 'Client Satisfaction' },
];

/* ============================== HELPERS ============================== */

function Reveal({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) { setShown(true); io.disconnect(); } },
      { threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className={className}
      style={{ opacity: shown ? 1 : 0, transform: shown ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity .6s ease ${delay}ms, transform .6s ease ${delay}ms` }}>
      {children}
    </div>
  );
}

/* ============================== PAGE ============================== */

const Solutions = () => {
  const [active, setActive] = useState('All Solutions');

  const visible = SOLUTIONS.filter((s) => active === 'All Solutions' || s.category === active);

  return (
    <div className="bg-white min-h-screen overflow-x-hidden">
      <style>{`
        @keyframes seFadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        .se-fade-up { animation: seFadeUp .7s ease-out both; }
        /* Hero image: absolute overlay on desktop so resizing it never shifts the layout */
        @media (min-width: 1024px) {
          .se-hero-img {
            position: absolute;
            top: calc(50% + var(--hero-y, 0px));
            right: var(--hero-x, 0px);
            transform: translateY(-50%);
            width: var(--hero-w, 600px);
            height: var(--hero-h, auto);
            max-width: none;
            margin: 0;
          }
        }
      `}</style>

      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#eef4fb] via-[#f5f9fd] to-white">
        <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />
        <div className={`${CONTAINER} relative py-8 lg:py-12`}>
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div className="se-fade-up">
              <nav className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                <Link to="/" className="hover:text-primary">Home</Link>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-primary font-medium">Solutions</span>
              </nav>

              <span className="inline-flex items-center text-xs font-bold tracking-[0.14em] uppercase text-primary bg-white ring-1 ring-primary/25 px-4 py-1.5 rounded-full mb-5">
                Smart Solutions for Smart Businesses
              </span>

              <h1 className="text-4xl sm:text-5xl xl:text-6xl font-extrabold leading-[1.08] tracking-tight" style={{ color: '#13294b' }}>
                Intelligent Solutions{' '}
                <span className="bg-gradient-to-r from-[#18469A] via-[#1B83C5] to-[#21BEE1] bg-clip-text text-transparent">That Drive Results</span>
              </h1>
              <p className="mt-5 text-lg text-slate-500 max-w-lg leading-relaxed">
                SmartEdz builds end-to-end software solutions to help businesses automate operations, enhance
                productivity, and achieve sustainable growth.
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8 max-w-xl">
                {FEATURES.map((f) => (
                  <div key={f.top}>
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-2">
                      <f.Icon className="w-5 h-5 text-primary" />
                    </div>
                    <div className="font-bold text-slate-800 text-sm leading-tight mb-1">{f.top}</div>
                    <div className="text-[11px] text-slate-500 leading-snug">{f.bottom}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="hidden sm:block se-fade-up relative lg:min-h-[500px]" style={{ animationDelay: '.15s' }}>
              <img
                src="/Solutions_Img.png"
                alt="SmartEdz solutions preview"
                className="se-hero-img w-full h-auto max-w-[900px] mx-auto block select-none drop-shadow-2xl"
                draggable="false"
                style={{
                  '--hero-w': HERO_IMAGE.desktopWidth,
                  '--hero-h': HERO_IMAGE.desktopHeight,
                  '--hero-x': HERO_IMAGE.offsetX,
                  '--hero-y': HERO_IMAGE.offsetY,
                }}
                onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ================= SOLUTIONS ================= */}
      <section className={`${CONTAINER} py-14`}>
        <Reveal>
          <div className="text-center mb-8">
            <span className="block text-xs font-bold tracking-[0.2em] uppercase text-primary mb-3">Our Solutions</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: '#13294b' }}>Complete Solutions for Every Business Need</h2>
          </div>
        </Reveal>

        {/* filter tabs (scrollable) */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-2 mb-8">
          {CATEGORIES.map((c) => {
            const isActive = active === c.label;
            return (
              <button
                key={c.label}
                onClick={() => setActive(c.label)}
                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
                  isActive ? 'bg-primary text-white shadow' : 'text-slate-600 bg-white ring-1 ring-slate-200 hover:bg-slate-50'
                }`}
              >
                <c.Icon className="w-4 h-4" />
                {c.label}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {visible.map((s, i) => (
            <Reveal key={s.title} delay={(i % 5) * 50}>
              <div className="group h-full flex flex-col bg-white rounded-2xl ring-1 ring-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 p-6">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110" style={{ backgroundColor: `${s.color}1A` }}>
                  <s.Icon className="w-6 h-6" style={{ color: s.color }} />
                </div>
                <h3 className="font-bold text-slate-800 text-[15px] leading-snug mb-2">{s.title}</h3>
                <p className="text-[13px] text-slate-500 leading-relaxed flex-1">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ================= WHY CHOOSE (STATS) ================= */}
      <section className={`${CONTAINER} pb-14`}>
        <Reveal className="rounded-2xl bg-[#f5f9fd] ring-1 ring-slate-200 px-6 py-8">
          <h2 className="text-center text-2xl font-extrabold mb-8" style={{ color: '#13294b' }}>Why Choose SmartEdz Solutions?</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-y-8 gap-x-4">
            {STATS.map((s) => (
              <div key={s.label} className="flex items-center gap-3 justify-center">
                <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <s.Icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="text-xl font-extrabold text-primary leading-none">{s.value}</div>
                  <div className="text-[11px] text-slate-500 mt-1">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* ================= CTA BANNER ================= */}
      <section className={`${CONTAINER} pb-16`}>
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#0A1B3D] via-[#0f2e6b] to-[#123a86] px-6 sm:px-10 py-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div className="text-center lg:text-left">
              <h3 className="text-2xl font-extrabold text-white">Need a Solution for Your Business?</h3>
              <p className="text-slate-300 mt-1">Let's discuss how we can help you achieve your goals.</p>
            </div>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/request-demo" className="inline-flex items-center gap-2 rounded-lg px-6 py-3.5 font-semibold text-primary bg-white hover:bg-slate-100 transition whitespace-nowrap">
                Schedule a Free Consultation <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact-us" className="inline-flex items-center gap-2 rounded-lg px-6 py-3.5 font-semibold text-white ring-1 ring-white/40 hover:bg-white/10 transition whitespace-nowrap">
                <Phone className="w-4 h-4" /> Talk to Our Expert
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Solutions;